import { useCallback, useEffect, useState } from 'react'
import { Pagination } from '@/api/Pagination/Types/Pagination'
import { CompanyTag } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { ClientDto, ClientDtoKey } from 'api/Clients/Types/Client'
import { CreateFilter } from 'api/Identity/Types/CreateFilter'
import { PageData } from 'api/Pagination/Types'
import { FilterType } from 'components/Filter'
import { getClientFilterFields } from 'components/Filter/ClientFields'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { Spinner } from 'components/Spinner'
import { createGridState } from 'features/DataGrid'
import { useGridStatusState } from 'features/DataGrid/hooks'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchClientStatusAction, fetchClientTypesAction } from 'store/clients/clients.actions'
import { GridFilterActionState, selectGridFilters } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import {
  fetchFilterAction,
  fetchFilterTypesAction,
  removeFilterAction,
  saveFilterAction,
} from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery } from 'utils/QueryHelpers'

const useMapStateToProps = () => ({
  clientTypes: useAppSelector((state) => state.clients.types),
  clientStatus: useAppSelector((state) => state.clients.status),
  filters: useAppSelector((state) => state.identity.filter),
  filterTypes: useAppSelector((state) => state.identity.filterType),
  gridState: useAppSelector((state) => state.grid),
  hasFilters: useAppSelector(selectGridFilters).length > 0,
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)
  return {
    fetchClientTypes: () => dispatch(fetchClientTypesAction(clientsApiClient)),
    fetchClientStatus: () => dispatch(fetchClientStatusAction(clientsApiClient)),
    fetchFilterTypes: () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: () => dispatch(fetchFilterAction(identityApiClient)),
    saveFilter: (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    removeFilter: (id: string) => dispatch(removeFilterAction(id, identityApiClient)),
  }
}

interface SelectClientProps {
  getClient: (client: ClientDto) => void
  open: DialogPrimitive.Root.Props['open']
  onOpenChange: DialogPrimitive.Root.Props['onOpenChange']
}

export const SelectClientModal = (props: SelectClientProps): JSX.Element => {
  const clientsApiClient = useApiClient(ClientsApiClient)
  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const [gridFilterState, setGridFilterState] = useState<GridFilterActionState>(
    getPaginationInitialState()
  )
  const [clients, setClients] = useState<ClientDto[]>([])
  const { filters, filterTypes, clientTypes, clientStatus } = useMapStateToProps()
  const { fetchFilters, fetchFilterTypes, fetchClientTypes, fetchClientStatus } =
    useMapDispatchToProps()

  useEffect(() => {
    if (!props.open) return
    ;(async () => {
      await fetchGrid(
        async (gridFilterActionState: GridFilterActionState): Promise<PageData<ClientDto[]>> => {
          const searchQuery = getQuery(gridFilterActionState.filter)
          const response = await clientsApiClient.getClients({
            search: searchQuery,
            pageIndex: gridFilterActionState.pageIndex,
            pageSize: gridFilterActionState.pageSize,
          })

          if (isEmpty(clientTypes)) await fetchClientTypes()
          if (isEmpty(clientStatus)) await fetchClientStatus()
          if (isEmpty(filterTypes)) await fetchFilterTypes()
          if (isEmpty(filters)) await fetchFilters()

          if (response?.items) {
            setClients(response.items)
          }

          return response
        }
      )
    })()
  }, [props.open]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<ClientDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridState = createGridState(getPaginationInitialState(), gridFilterState)
    const response = await callback(gridState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      setGridFilterState({
        ...gridState,
        pageIndex: gridState.pageIndex,
        pageSize: gridState.take,
        skip: gridState.skip,
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      const gridFilter = filter ?? gridFilterState.filter
      return (
        buildQuery({
          ...normalizeQuery(gridFilter),
          logic: gridFilter?.logic ?? 'or',
        }) ?? null
      )
    },
    [gridFilterState.filter]
  )

  const filterFetch = useCallback(
    async (params?: {
      query?: string | null
      filter?: CompositeFilterDescriptor
    }): Promise<void> => {
      const searchQuery =
        params?.query !== undefined ? params.query : getQuery(gridFilterState.filter)
      const pageIndex = 1
      const pageSize = gridFilterState.pageSize ?? 10
      const take = gridFilterState.take ?? 10
      const skip = 0

      const response = await clientsApiClient.getClients({
        search: searchQuery,
        pageIndex,
        pageSize,
      })
      const total = response.totalResults

      if (response?.items) {
        setClients(response.items.map((dataItem: ClientDto) => ({ ...dataItem, selected: false })))
      }
      setGridFilterState({
        ...gridFilterState,
        filter: params?.filter ?? gridFilterState.filter,
        take,
        skip,
        total,
        pageIndex,
      })
    },
    [getQuery, gridFilterState] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const handlePageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip: event.page.skip,
      recordsPerPage: take,
    })

    const searchQuery = getQuery()
    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
    })

    const response = await clientsApiClient.getClients({
      search: searchQuery,
      pageIndex,
      pageSize,
    })
    const total = response.totalResults

    if (response?.items) {
      setClients(response.items)
    }
    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
      total,
    })
  }
  const handleClient = (dataItem: ClientDto) => {
    props.getClient(dataItem)
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent size='4xl'>
        <DialogHeader>
          <DialogTitle>Select Client</DialogTitle>
          {!gridStatusState.isLoading && (
            <SelectFiltersFacade
              gridState={gridFilterState}
              gridStatusActions={gridStatusActions}
              gridStatusState={gridStatusState}
              setGridFilters={(next) => setGridFilterState((prev) => ({ ...prev, ...next }))}
              filterFetch={filterFetch}
              filterType={FilterType.CLIENT}
              filterFields={getClientFilterFields()}
              containerClassName='records-clients__filter'
              getQuery={getQuery}
            />
          )}
        </DialogHeader>
        {gridStatusState.isLoading ? (
          <Spinner />
        ) : (
          <Grid
            resizable
            selectedField='selected'
            data={clients}
            {...gridFilterState}
            sortable={{
              allowUnsort: true,
              mode: 'multiple',
            }}
            total={gridFilterState.total}
            take={gridFilterState.take}
            skip={gridFilterState.skip}
            onPageChange={handlePageChange}
            pageable={pageableSettings}
            style={{ maxHeight: '70vh' }}
          >
            <Column
              field='Select'
              width={130}
              cell={(gridCellProps: GridCellProps) => (
                <td data-test='select-column'>
                  <Button
                    onClick={() => handleClient(gridCellProps.dataItem)}
                    data-test='select-button'
                  >
                    Select
                  </Button>
                </td>
              )}
            />
            <Column minResizableWidth={250} width={250} field={ClientDtoKey.name} title='Name' />
            <Column
              minResizableWidth={120}
              width={120}
              field={ClientDtoKey.type}
              title='Client type'
            />
            <Column
              minResizableWidth={120}
              width={120}
              field={ClientDtoKey.status}
              title='Status'
            />
            <Column
              minResizableWidth={120}
              width={120}
              field={ClientDtoKey.country}
              title='Country'
            />
            <Column
              minResizableWidth={200}
              width={200}
              field={ClientDtoKey.emailAddress}
              title='Email'
            />
            <Column minResizableWidth={120} width={120} field={ClientDtoKey.phone} title='Phone' />
            <Column
              minResizableWidth={80}
              width={80}
              field={ClientDtoKey.tickmillCompany}
              cell={(gridCellProps: GridCellProps) => {
                if (!gridCellProps.dataItem?.tickmillCompany?.id) {
                  return <td />
                }
                return (
                  <td>
                    <CompanyTag id={gridCellProps.dataItem.tickmillCompany.id}>
                      {gridCellProps.dataItem.tickmillCompany.name}
                    </CompanyTag>
                  </td>
                )
              }}
              title='Entity'
            />
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  )
}

const normalizeQuery = (filter?: FilterDescriptor[] | CompositeFilterDescriptor) => {
  if (!filter) {
    return {
      logic: 'and',
      filters: [],
    }
  }

  const gridFilterDescriptor = filter as CompositeFilterDescriptor

  const filters = gridFilterDescriptor.filters.map(
    (filterDescriptor: FilterDescriptor | CompositeFilterDescriptor) => {
      return filterDescriptor as FilterDescriptor
    }
  )

  return { ...gridFilterDescriptor, filters }
}
